import { ChevronRight, ListCollapse, Star } from "lucide-react";
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useLocation, useParams } from "react-router-dom";

import { ExpandableSectionHeader } from "./ExpandableSectionHeader";
import { SidebarFavoriteStar } from "./SidebarFavoriteStar";
import { useTreeNavigation } from "./hooks/useTreeNavigation";
import { getChildEntities } from "./utils";

import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { FavoriteItem, useListFavorites } from "@/queries/favorites";
import { useListFolderEntities } from "@/queries/folder-entities";
import { Entity, EntityType } from "@/queries/folder-entities/types";
import { routePaths } from "@/utils/routePaths";

interface FavoritesSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
  /** Reports sticky header height so Home can stack beneath Favorites. */
  onStickyHeaderHeightChange?: (height: number) => void;
}

interface FavoritesRowEntity {
  entity_type: EntityType;
  entity_id: string;
  name: string;
}

const toRowEntity = (favorite: FavoriteItem): FavoritesRowEntity => ({
  entity_type: favorite.entity_type,
  entity_id: favorite.entity_id,
  name: favorite.name,
});

const entityToRow = (entity: Entity): FavoritesRowEntity => ({
  entity_type: entity.type,
  entity_id: String(entity.id),
  name: entity.name,
});

const FavoriteTreeRow = ({
  entity,
  depth,
  isActive,
  isExpanded,
  hasChildren,
  isLoadingChildren,
  onNavigate,
  onToggleFolder,
  getItemIcon,
  expandedFolders,
  allEntities,
  isEntityActive,
  onNavigateEntity,
}: {
  entity: FavoritesRowEntity;
  depth: number;
  isActive: boolean;
  isExpanded: boolean;
  hasChildren: boolean;
  isLoadingChildren: boolean;
  onNavigate: () => void;
  onToggleFolder: (folderId: number) => void;
  getItemIcon: (item: Entity, isCollapsed?: boolean) => ReactNode;
  expandedFolders: Set<number>;
  allEntities: Entity[];
  isEntityActive: (entity: FavoritesRowEntity) => boolean;
  onNavigateEntity: (entity: FavoritesRowEntity) => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const isFolder = entity.entity_type === EntityType.FOLDER;
  const folderId = Number(entity.entity_id);

  const handleToggle = (e: MouseEvent) => {
    e.stopPropagation();
    if (!Number.isNaN(folderId)) {
      onToggleFolder(folderId);
    }
  };

  const iconEntity = {
    type: entity.entity_type,
    id: entity.entity_id,
    name: entity.name,
    path: [],
  } as Entity;

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className={cn(
          "group/treeItem relative flex cursor-pointer select-none items-center rounded-md px-2 py-1 text-sm transition-all duration-200",
          isActive
            ? "bg-gray-100 font-medium text-gray-900"
            : "text-gray-700 hover:bg-gray-50 hover:text-gray-900",
        )}
        style={{ paddingLeft: `${8 + depth * 18}px` }}
        onClick={onNavigate}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onNavigate();
          }
        }}
      >
        <div className="relative min-w-0 flex-1 overflow-hidden">
          {isFolder && (
            <span
              role="presentation"
              aria-hidden="true"
              className={cn(
                "pointer-events-none absolute left-0 z-10 flex h-4 w-4 items-center justify-center",
                "opacity-0 transition-opacity duration-150",
                "group-hover/treeItem:pointer-events-auto group-hover/treeItem:opacity-100",
                "group-focus-within/treeItem:pointer-events-auto group-focus-within/treeItem:opacity-100",
              )}
              onClick={handleToggle}
              onPointerDown={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
            >
              <ChevronRight
                className={cn(
                  "h-4 w-4 text-black transition-transform duration-200",
                  isExpanded && "rotate-90",
                )}
                strokeWidth={2}
              />
            </span>
          )}
          <div
            className={cn(
              "flex min-w-0 flex-1 items-center gap-1.5",
              isFolder &&
                "transition-transform duration-150 group-focus-within/treeItem:translate-x-[18px] group-hover/treeItem:translate-x-[18px]",
            )}
          >
            {getItemIcon(iconEntity, !isExpanded || !hasChildren)}
            <OverflowDisplayTooltip
              content={entity.name}
              className={cn("min-w-0 flex-1 text-[13px]", isActive && "font-semibold")}
              side="right"
              delayDuration={500}
            >
              {entity.name}
            </OverflowDisplayTooltip>
          </div>
        </div>
        <SidebarFavoriteStar
          entityType={entity.entity_type}
          entityId={entity.entity_id}
          isVisible={isHovered}
        />
      </div>

      {isFolder && isExpanded && (
        <div>
          {isLoadingChildren ? (
            <div className="space-y-1 py-1" style={{ paddingLeft: `${8 + (depth + 1) * 18}px` }}>
              <Skeleton className="h-7 w-full rounded-md" />
              <Skeleton className="h-7 w-11/12 rounded-md" />
            </div>
          ) : (
            getChildEntities(allEntities, folderId).map(child => {
              const childRow = entityToRow(child);
              const childFolderId =
                child.type === EntityType.FOLDER && typeof child.id === "number"
                  ? child.id
                  : undefined;
              const childExpanded =
                childFolderId !== undefined && expandedFolders.has(childFolderId);
              const childChildren =
                childFolderId !== undefined ? getChildEntities(allEntities, childFolderId) : [];

              return (
                <FavoriteTreeRow
                  key={`${childRow.entity_type}-${childRow.entity_id}`}
                  entity={childRow}
                  depth={depth + 1}
                  isActive={isEntityActive(childRow)}
                  isExpanded={childExpanded}
                  hasChildren={childChildren.length > 0}
                  isLoadingChildren={false}
                  onNavigate={() => onNavigateEntity(childRow)}
                  onToggleFolder={onToggleFolder}
                  getItemIcon={getItemIcon}
                  expandedFolders={expandedFolders}
                  allEntities={allEntities}
                  isEntityActive={isEntityActive}
                  onNavigateEntity={onNavigateEntity}
                />
              );
            })
          )}
        </div>
      )}
    </>
  );
};

export const FavoritesSection = ({
  isExpanded,
  onToggle,
  onStickyHeaderHeightChange,
}: FavoritesSectionProps) => {
  const params = useParams();
  const location = useLocation();
  const { navigate, getItemIcon } = useTreeNavigation();
  const workspaceId = Number(params.workspaceId);
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
  const stickyHeaderRef = useRef<HTMLDivElement>(null);

  const { data } = useListFavorites({
    workspaceId,
    enabled: !!workspaceId,
  });

  const favorites = data?.favorites ?? [];
  const hasFolderFavorites = favorites.some(favorite => favorite.entity_type === EntityType.FOLDER);

  const { data: hierarchyData, isLoading: isHierarchyLoading } = useListFolderEntities({
    folder_id: null,
    flatten: true,
    enabled: !!workspaceId && hasFolderFavorites,
    keepPreviousData: true,
  });

  const allEntities = hierarchyData?.entities ?? [];

  // Sticky header must be a direct scroll-container child (not inside Collapsible)
  // or it unsticks when the Favorites block scrolls away.
  useLayoutEffect(() => {
    if (favorites.length === 0) {
      onStickyHeaderHeightChange?.(0);
      return undefined;
    }

    const el = stickyHeaderRef.current;
    if (!el) {
      return undefined;
    }

    const update = () => {
      onStickyHeaderHeightChange?.(el.getBoundingClientRect().height);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      observer.disconnect();
      onStickyHeaderHeightChange?.(0);
    };
  }, [favorites.length, onStickyHeaderHeightChange]);

  const getFavoritePath = useCallback(
    (entity: FavoritesRowEntity) =>
      routePaths.getEntityPath(workspaceId, entity.entity_type, entity.entity_id),
    [workspaceId],
  );

  const isEntityActive = useCallback(
    (entity: FavoritesRowEntity) => {
      const path = getFavoritePath(entity);
      return location.pathname === path || location.pathname.startsWith(`${path}/`);
    },
    [getFavoritePath, location.pathname],
  );

  const onNavigateEntity = useCallback(
    (entity: FavoritesRowEntity) => {
      navigate(getFavoritePath(entity));
    },
    [getFavoritePath, navigate],
  );

  const toggleFolder = useCallback((folderId: number) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const collapseAllFolders = useCallback(() => {
    setExpandedFolders(new Set());
  }, []);

  const scrollFavoritesToTop = useCallback(() => {
    const scrollContainer = stickyHeaderRef.current?.closest(
      "[data-scroll-container]",
    ) as HTMLElement | null;
    scrollContainer?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Label/tile click: always scroll to top; does not expand/collapse.
  const scrollFavoritesIntoView = useCallback(() => {
    scrollFavoritesToTop();
  }, [scrollFavoritesToTop]);

  const handleToggle = useCallback(() => {
    // Chevron: expanding also scrolls to top so Favorites content is visible.
    if (!isExpanded) {
      scrollFavoritesToTop();
    }
    onToggle();
  }, [isExpanded, onToggle, scrollFavoritesToTop]);

  if (favorites.length === 0) {
    return null;
  }

  const collapseAction =
    expandedFolders.size > 0 ? (
      <BasicTooltip content="Collapse all folders">
        <button
          type="button"
          aria-label="Collapse all folders"
          className={cn(
            "flex h-5 w-5 items-center justify-center rounded-sm text-gray-400",
            "opacity-0 transition-opacity duration-150 hover:bg-gray-200 hover:text-gray-700",
            "group-focus-within/section:opacity-100 group-hover/section:opacity-100",
          )}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            collapseAllFolders();
          }}
          onMouseDown={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <ListCollapse className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      </BasicTooltip>
    ) : null;

  return (
    <>
      <ExpandableSectionHeader
        ref={stickyHeaderRef}
        label={`Favorites (${favorites.length})`}
        icon={<Star className="h-3.5 w-3.5" strokeWidth={2} />}
        isExpanded={isExpanded}
        onToggle={handleToggle}
        // Label scrolls to top; chevron toggles (and scrolls when expanding).
        onNavigate={scrollFavoritesIntoView}
        sticky
        stickyTopPx={0}
        stickyZIndex={20}
        trailingAction={collapseAction}
      />
      <Collapsible open={isExpanded}>
        <CollapsibleContent>
          <div className="relative ml-[15px]">
            {favorites.map(favorite => {
              const row = toRowEntity(favorite);
              const isFolder = row.entity_type === EntityType.FOLDER;
              const folderId = Number(row.entity_id);
              const folderExpanded =
                isFolder && !Number.isNaN(folderId) && expandedFolders.has(folderId);
              const childCount =
                isFolder && !Number.isNaN(folderId)
                  ? getChildEntities(allEntities, folderId).length
                  : 0;

              return (
                <FavoriteTreeRow
                  key={`${row.entity_type}-${row.entity_id}`}
                  entity={row}
                  depth={0}
                  isActive={isEntityActive(row)}
                  isExpanded={folderExpanded}
                  hasChildren={isFolder && (isHierarchyLoading || childCount > 0)}
                  isLoadingChildren={
                    folderExpanded && isHierarchyLoading && allEntities.length === 0
                  }
                  onNavigate={() => onNavigateEntity(row)}
                  onToggleFolder={toggleFolder}
                  getItemIcon={getItemIcon}
                  expandedFolders={expandedFolders}
                  allEntities={allEntities}
                  isEntityActive={isEntityActive}
                  onNavigateEntity={onNavigateEntity}
                />
              );
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </>
  );
};
